import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Modal, Form, Select, message, Tag, Popconfirm, InputNumber, Typography, Alert, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import evmService from '../../../shared/services/evmService';
import { formatDateTime } from '../../../shared/utils/date';

const { Option } = Select;
const { Title, Text } = Typography;

const MyPortForward = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [forwards, setForwards] = useState([]);
    const [vms, setVms] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchForwards();
        fetchVms();
    }, [pagination.current]);

    const fetchForwards = async () => {
        setLoading(true);
        try {
            const response = await evmService.getPortForwardList({
                page: pagination.current,
                per_page: pagination.pageSize,
            });
            if (response.status === 'success') {
                setForwards(response.data?.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data?.total || 0,
                }));
            }
        } catch (error) {
            message.error(t('Failed to load port forwarding rules'));
        }
        setLoading(false);
    };

    const fetchVms = async () => {
        try {
            const response = await evmService.getVmList({ per_page: 100 });
            if (response.status === 'success') {
                setVms(response.data?.items || response.data?.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch VMs');
        }
    };

    const handleCreate = () => {
        form.resetFields();
        form.setFieldsValue({
            protocol: 'tcp',
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await evmService.deletePortForward(id);
            if (response.status === 'success') {
                message.success(t('Port forwarding deleted'));
                fetchForwards();
            } else {
                message.error(response.msg || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const response = await evmService.createPortForward(values);

            if (response.status === 'success') {
                message.success(t('Port forwarding rule created'));
                setModalVisible(false);
                fetchForwards();
            } else {
                message.error(response.msg || t('Creation failed'));
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(t('Creation failed'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: t('Virtual machine'),
            dataIndex: 'vm_id',
            render: (vmId) => {
                const vm = vms.find(v => v.id === vmId);
                return vm ? (
                    <Space direction="vertical" size={0}>
                        <span>{vm.hostname || `VM-${vmId}`}</span>
                        <Text type="secondary" style={{ fontSize: 12 }}>{vm.ip_address}</Text>
                    </Space>
                ) : `VM ${vmId}`;
            },
        },
        {
            title: t('Protocol'),
            dataIndex: 'protocol',
            width: 80,
            render: (protocol) => (
                <Tag color={protocol === 'tcp' ? 'blue' : 'green'}>
                    {protocol?.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: t('Public network'),
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <span>{record.external_ip}:{record.external_port}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Public network')}</Text>
                </Space>
            ),
        },
        {
            title: t('Private network'),
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <span>{record.internal_ip}:{record.internal_port}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Private network')}</Text>
                </Space>
            ),
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            width: 100,
            render: (status) => {
                const map = {
                    active: { color: 'success', text: t('Normal') },
                    running: { color: 'success', text: t('Normal') },
                    pending: { color: 'processing', text: t('Not yet effective') },
                    creating: { color: 'processing', text: t('Creating') },
                    inactive: { color: 'default', text: t('Disabled') },
                    disabled: { color: 'default', text: t('Disabled') },
                    error: { color: 'error', text: t('Error') },
                    failed: { color: 'error', text: t('Error') },
                };
                const info = map[status] || { color: 'default', text: status || t('Normal') };
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            width: 160,
            render: (date) => formatDateTime(date),
        },
        {
            title: t('Actions'),
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this port forwarding rule?')}
                    description={t('Traffic will no longer be forwarded to {{internal_ip}}:{{internal_port}}, and related inbound connections will be interrupted.', { internal_ip: record.internal_ip, internal_port: record.internal_port })}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('Delete')}
                    okType="danger"
                    cancelText={t('Cancel')}
                >
                    <Button type="link" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>{t('Port forwarding')}</Title>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchForwards}>{t('Refresh')}</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            {t('Add rule')}
                        </Button>
                    </Space>
                </div>

                <Alert
                    message={t('About port forwarding')}
                    description={t('Port forwarding allows external traffic to access a virtual machine\'s internal services through NAT. It is useful for virtual machines with only private IPs.')}
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginBottom: 16 }}
                />

                <Table
                    columns={columns}
                    dataSource={forwards}
                    rowKey="id"
                    loading={loading}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={t('No port forwarding rules yet')}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleCreate}
                                >
                                    {t('Create your first rule')}
                                </Button>
                            </Empty>
                        )
                    }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => t('{{total}} rules total', { total: total }),
                        onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
                    }}
                />
            </Card>

            <Modal
                title={t('Create port forwarding rule')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                okText={t('Create')}
                cancelText={t('Cancel')}
                okButtonProps={{ loading: submitting }}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="vm_id"
                        label={t('Target virtual machine')}
                        rules={[{ required: true, message: t('Select a virtual machine') }]}
                    >
                        <Select
                            placeholder={t('Select a virtual machine')}
                            optionFilterProp="children"
                            showSearch
                            notFoundContent={t('No instances need port forwarding (only NAT or VPC-only instances do)')}
                        >
                            {/* only VMs without their own public IPv4 (NAT plans / VPC-only) need port forwarding */}
                            {vms.filter((v) => v.forwardable !== false).map(vm => (
                                <Option key={vm.id} value={vm.id}>
                                    {vm.hostname || `VM-${vm.id}`} ({vm.ip_address || t('No IP')})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="protocol"
                        label={t('Protocol')}
                        rules={[{ required: true, message: t('Select a protocol') }]}
                    >
                        <Select>
                            <Option value="tcp">TCP</Option>
                            <Option value="udp">UDP</Option>
                        </Select>
                    </Form.Item>

                    <Space style={{ width: '100%' }} size="large">
                        <Form.Item
                            name="external_port"
                            label={t('Public port')}
                            rules={[
                                { type: 'number', min: 1, max: 65535, message: t('Port must be between 1-65535') }
                            ]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={65535}
                                style={{ width: '100%' }}
                                placeholder={t('Leave empty to auto-assign')}
                            />
                        </Form.Item>

                        <Form.Item
                            name="internal_port"
                            label={t('Private network port')}
                            rules={[
                                { required: true, message: t('Please enter a private port') },
                                { type: 'number', min: 1, max: 65535, message: t('Port must be between 1-65535') }
                            ]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={65535}
                                style={{ width: '100%' }}
                                placeholder={t('E.g. 80')}
                            />
                        </Form.Item>
                    </Space>

                    <Alert
                        message={t('How it works')}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li>{t('Access')} <code>{t('Public IP:external port')}</code> {t('traffic will be forwarded to')} <code>{t('Virtual machine private IP:private port')}</code></li>
                                <li>{t('Please ensure the destination port is not blocked by a firewall or security group')}</li>
                                <li>{t('Common ports: 22 (SSH), 80 (HTTP), 443 (HTTPS), 3306 (MySQL)')}</li>
                            </ul>
                        }
                        type="warning"
                        style={{ marginTop: 16 }}
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default MyPortForward;
